import React, { useState, useEffect } from 'react';
import { clinicService } from '../services/api';

const CACHE_KEY = 'schedule_expiry_cache';
const TTL_MS = 60 * 60 * 24 * 1000; // 10 seconds (testing)
const INVALIDATE_EVENT = 'schedule_expiry_cache_invalidated';

export const invalidateScheduleExpiryCache = () => {
  localStorage.removeItem(CACHE_KEY);
  window.dispatchEvent(new Event(INVALIDATE_EVENT));
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { timestamp, data } = JSON.parse(raw);
    if (Date.now() - timestamp > TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const writeCache = (data) => {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
};

const ScheduleExpiryBanner = () => {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const cached = readCache();
      if (cached) {
        if (!cancelled) setItems(cached);
        return;
      }
      try {
        const response = await clinicService.getExpiringSchedules(30);
        const data = response.items || [];
        writeCache(data);
        if (!cancelled) setItems(data);
      } catch {
        // silently ignore — banner is best-effort
      }
    };

    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    const onInvalidate = () => setRefreshKey((k) => k + 1);
    window.addEventListener(INVALIDATE_EVENT, onInvalidate);
    return () => window.removeEventListener(INVALIDATE_EVENT, onInvalidate);
  }, []);

  if (dismissed || items.length === 0) return null;

  const expired = items.filter((i) => i.expired);
  const expiring = items.filter((i) => !i.expired);

  return (
    <div style={styles.banner}>
      <div style={styles.header}>
        <strong>Doctor Schedule Expiry Warning</strong>
        <button style={styles.close} onClick={() => setDismissed(true)}>×</button>
      </div>
      {expired.length > 0 && (
        <div>
          <div style={{ ...styles.label, color: '#c0392b' }}>Expired</div>
          <ul style={styles.list}>
            {expired.map((item) => (
              <li key={item.schedule_id} style={styles.item}>
                <span style={styles.clid}>[{item.clid}]</span> {item.doctor_name} — expired {Math.abs(item.days_left)} day{Math.abs(item.days_left) !== 1 ? 's' : ''} ago ({item.to_date})
              </li>
            ))}
          </ul>
        </div>
      )}
      {expiring.length > 0 && (
        <div>
          <div style={{ ...styles.label, color: '#e67e22' }}>Expiring soon</div>
          <ul style={styles.list}>
            {expiring.map((item) => (
              <li key={item.schedule_id} style={styles.item}>
                <span style={styles.clid}>[{item.clid}]</span> {item.doctor_name} — expires in {item.days_left} day{item.days_left !== 1 ? 's' : ''} ({item.to_date})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const styles = {
  banner: {
    background: '#fff8e1',
    border: '1px solid #f0c040',
    borderLeft: '4px solid #e67e22',
    borderRadius: 4,
    padding: '12px 16px',
    marginBottom: 16,
    fontSize: 13,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  close: {
    background: 'none',
    border: 'none',
    fontSize: 18,
    cursor: 'pointer',
    lineHeight: 1,
    padding: '0 4px',
    color: '#555',
  },
  label: {
    fontWeight: 600,
    marginBottom: 4,
  },
  list: {
    margin: '0 0 8px 0',
    paddingLeft: 20,
  },
  item: {
    marginBottom: 2,
  },
  clid: {
    fontFamily: 'monospace',
    color: '#555',
  },
};

export default ScheduleExpiryBanner;
